import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(path, payload);
      setUser(data.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1542744173-05336fcc7ad4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxkYXNoYm9hcmQlMjB1c2VyJTIwd29ya2luZ3xlbnwwfHx8fDE3ODY0MDQxNjN8MA&ixlib=rb-4.1.0&q=85"
          alt="Educação financeira" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/85" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center">
            <img src="/alligare-logo-full-white.png" alt="Alligare Consultoria de Recursos Humanos" data-testid="brand-logo-hero"
              className="h-14 w-auto object-contain" />
          </div>
          <div>
            <h1 className="font-heading text-4xl lg:text-5xl font-extrabold leading-none tracking-tight">
              Domine sua vida financeira.
            </h1>
            <p className="mt-6 text-white/80 font-body text-base max-w-md leading-relaxed">
              Controle finanças pessoais e empresariais, importe extratos e aprenda com os maiores
              bestsellers de educação financeira.
            </p>
          </div>
          <p className="text-white/50 text-sm font-body">Consultoria de Recursos Humanos · Educação Financeira</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="w-full max-w-md">
          <div className="lg:hidden flex items-center mb-8">
            <img src="/alligare-logo-full.png" alt="Alligare Consultoria de Recursos Humanos" data-testid="brand-logo-mobile"
              className="h-12 w-auto object-contain" />
          </div>
          <p className="uppercase text-xs tracking-[0.2em] text-text-muted font-body">
            {mode === "login" ? "Bem-vindo de volta" : "Comece agora"}
          </p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-text-primary mt-2">
            {mode === "login" ? "Entrar na conta" : "Criar sua conta"}
          </h2>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name" className="font-body">Nome</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-text-muted" />
                  <Input id="name" data-testid="name-input" className="pl-9 rounded-md" required
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Seu nome" />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="font-body">E-mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-text-muted" />
                <Input id="email" type="email" data-testid="email-input" className="pl-9 rounded-md" required
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="voce@email.com" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="font-body">Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-text-muted" />
                <Input id="password" type="password" data-testid="password-input" className="pl-9 rounded-md" required
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
              </div>
            </div>

            {error && <p data-testid="auth-error" className="text-sm text-secondary font-body">{error}</p>}

            <Button type="submit" disabled={busy} data-testid="auth-submit"
              className="w-full rounded-md bg-primary hover:bg-primary/90 h-11 font-body group">
              {busy ? "Aguarde..." : mode === "login" ? "Entrar" : "Criar conta"}
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="h-px flex-1 bg-stone-200" />
            <span className="text-xs text-text-muted font-body">ou</span>
            <div className="h-px flex-1 bg-stone-200" />
          </div>

          <Button variant="outline" onClick={googleLogin} data-testid="google-login"
            className="w-full rounded-md h-11 font-body border-stone-300 hover:bg-stone-100">
            <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4 mr-2" />
            Continuar com Google
          </Button>

          <p className="mt-6 text-center text-sm text-text-secondary font-body">
            {mode === "login" ? "Não tem conta?" : "Já tem conta?"}{" "}
            <button data-testid="toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-primary font-semibold hover:underline">
              {mode === "login" ? "Cadastre-se" : "Entrar"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
